// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { ProductFormulationTypeProps } from 'interfaces';
import { renderProductFormulationTypes } from 'lib/helper';
import {
  ProductFormulationTypesPayload,
  useFindAllProductFormulationTypeSelectorLazyQuery,
} from 'generated/graphql';

const ProductFormulationTypes: FC<ProductFormulationTypeProps> = ({
  name,
  title,
  value,
  isActive,
  productTypeId,
  margin = 'dense',
  productSubTypeId,
  isClearable = false,
  handleChange,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ProductFormulationTypesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return renderProductFormulationTypes(options);
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllProductFormulationTypeSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllProductFormulationType } = data;
      const { data: productFormulationTypes, response, pagination } = findAllProductFormulationType || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((productFormulationTypes as ProductFormulationTypesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllProductFormulationTypeInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(productTypeId && { productTypeId }),
            ...(productSubTypeId && { subProductTypeId: productSubTypeId }),
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [fetchSelectorData, isActive, productSubTypeId, productTypeId],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default ProductFormulationTypes;
